import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import assetService from '../services/assetService';
import { ApiError } from '../services/apiService';

const ASSET_CODE_OPTIONS = [
  'creator-active-boosts',
  'creator-agreement',
  'creator-home-banners',
  'creator-login',
  'creator-mega-creator-day',
  'creator-register',
  'creator-share-merchant',
  'creator-share-novax8',
  'creator-share-skai1',
  'creator-upcoming-boosts',
  'skai1-home-banners',
  'skai1-invite',
  'skai1-register',
];

const ASSET_CODE_MULTIPLE_MAP = {
  'creator-active-boosts': false,
  'creator-agreement': false,
  'creator-home-banners': true,
  'creator-login': false,
  'creator-mega-creator-day': false,
  'creator-register': false,
  'creator-share-merchant': false,
  'creator-share-novax8': false,
  'creator-share-skai1': false,
  'creator-upcoming-boosts': false,
  'skai1-home-banners': true,
  'skai1-invite': false,
  'skai1-register': false,
};

const ASSET_CODE_TYPE_MAP = {
  'creator-active-boosts': 'F',
  'creator-agreement': 'T',
  'creator-home-banners': 'F',
  'creator-login': 'F',
  'creator-mega-creator-day': 'F',
  'creator-register': 'F',
  'creator-share-merchant': 'F',
  'creator-share-novax8': 'F',
  'creator-share-skai1': 'F',
  'creator-upcoming-boosts': 'F',
  'skai1-home-banners': 'F',
  'skai1-invite': 'F',
  'skai1-register': 'F',
};

const MasterAssetsPage = () => {
  const hasInitialLoadRef = useRef(false);

  // Table & Filter State
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  // Modal States
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [viewTab, setViewTab] = useState('en'); // 'en' or 'it'

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAsset, setDeletingAsset] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Auto-increment & Status Toggle State
  const [versionInfo, setVersionInfo] = useState(null);
  const [loadingVersionInfo, setLoadingVersionInfo] = useState(false);
  const [togglingStatusId, setTogglingStatusId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    titleIt: '',
    description: '',
    descriptionIt: '',
    number: 1,
    version: '1.0.0',
    type: 'T',
    multiple: false,
    contentPath: '',
    contentPathIt: '',
    contentValue: '',
    contentValueIt: '',
    ctaType: '',
    ctaUrl: '',
    status: 'A',
    required: false,
  });

  const [uploadingFileEn, setUploadingFileEn] = useState(false);
  const [uploadingFileIt, setUploadingFileIt] = useState(false);
  const [selectedFileEn, setSelectedFileEn] = useState(null);
  const [selectedFileIt, setSelectedFileIt] = useState(null);

  // Fetch Assets List
  const fetchAssets = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        page: params.page !== undefined ? params.page : pagination.page,
        size: params.size !== undefined ? params.size : pagination.size,
        keyword: params.keyword !== undefined ? params.keyword : searchTerm,
        type: params.type !== undefined ? params.type : filterType,
        status: params.status !== undefined ? params.status : filterStatus,
      };

      const response = await assetService.getAssetList(requestParams);
      if (response && response.data) {
        setAssets(response.data.data || []);
        setPagination({
          page: response.data.page || 1,
          pageCount: response.data.pageCount || 1,
          size: response.data.size || 20,
          count: response.data.count || 0,
          total: response.data.total || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
      if (err instanceof ApiError) {
        setError(`Failed to load assets: ${err.message}`);
      } else {
        setError('Failed to load assets. Please try again.');
      }
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, searchTerm, filterType, filterStatus]);

  // Initial Load
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      fetchAssets({ page: 1 });
    }
  }, [fetchAssets]);

  // Handle Search & Filter Apply
  const handleApplyFilter = e => {
    if (e) e.preventDefault();
    fetchAssets({ page: 1, keyword: searchTerm, type: filterType, status: filterStatus });
  };

  const handleResetFilter = () => {
    setSearchTerm('');
    setFilterType('');
    setFilterStatus('');
    fetchAssets({ page: 1, keyword: '', type: '', status: '' });
  };

  // Open Create Modal
  const handleOpenCreate = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormError(null);
    setVersionInfo(null);
    setSelectedFileEn(null);
    setSelectedFileIt(null);
    setShowSubmitConfirmModal(false);
    setFormData({
      code: '',
      title: '',
      titleIt: '',
      description: '',
      descriptionIt: '',
      number: 1,
      version: '1.0.0',
      type: 'F',
      multiple: false,
      contentPath: '',
      contentPathIt: '',
      contentValue: '',
      contentValueIt: '',
      ctaType: '',
      ctaUrl: '',
      status: 'A',
      required: false,
    });
    setShowFormModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = async asset => {
    setIsEditing(true);
    setEditingId(asset.id);
    setFormError(null);
    setVersionInfo(null);
    setSelectedFileEn(null);
    setSelectedFileIt(null);
    setShowSubmitConfirmModal(false);
    setFormData({
      code: asset.code || '',
      title: asset.title || '',
      titleIt: asset.titleIt || '',
      description: asset.description || '',
      descriptionIt: asset.descriptionIt || '',
      number: asset.number || 1,
      version: asset.version || '1.0.0',
      type: asset.type || 'T',
      multiple: Boolean(asset.multiple),
      contentPath: asset.contentPath || '',
      contentPathIt: asset.contentPathIt || '',
      contentValue: asset.contentValue || '',
      contentValueIt: asset.contentValueIt || '',
      ctaType: asset.ctaType || '',
      ctaUrl: asset.ctaUrl || '',
      status: asset.status || 'A',
      required: Boolean(asset.required),
    });
    setShowFormModal(true);
  };

  // Handle Asset Code Change & Auto-Increment
  const handleCodeChange = async selectedCode => {
    const isMulti = Boolean(ASSET_CODE_MULTIPLE_MAP[selectedCode]);
    const boundType = ASSET_CODE_TYPE_MAP[selectedCode] || 'F';
    setFormData(prev => ({
      ...prev,
      code: selectedCode,
      multiple: isMulti,
      type: boundType,
    }));

    if (!isEditing && selectedCode) {
      setLoadingVersionInfo(true);
      try {
        const res = await assetService.getNextVersion(selectedCode);
        if (res && res.data) {
          setVersionInfo(res.data);
          setFormData(prev => ({
            ...prev,
            number: res.data.nextNumber || 1,
            version: res.data.nextVersion || '1.0.0',
          }));
        }
      } catch (err) {
        console.error('Failed to get next version:', err);
      } finally {
        setLoadingVersionInfo(false);
      }
    }
  };

  // Handle Quick Status Toggle in Table
  const handleToggleStatus = async (asset, e) => {
    if (e) e.stopPropagation();
    const nextStatus = asset.status === 'A' ? 'N' : 'A';
    const nextStatusLabel = nextStatus === 'A' ? 'Active' : 'Inactive';
    setTogglingStatusId(asset.id);
    try {
      await assetService.toggleStatus(asset.id, nextStatus);
      setSuccessMessage(`Asset "${asset.code}" status changed to ${nextStatusLabel}`);
      setAssets(prev =>
        prev.map(item =>
          item.id === asset.id ? { ...item, status: nextStatus } : item
        )
      );
    } catch (err) {
      console.error('Failed to toggle status:', err);
      setError(err.message || 'Failed to update asset status');
    } finally {
      setTogglingStatusId(null);
    }
  };

  // Open View Modal
  const handleOpenView = asset => {
    setViewingAsset(asset);
    setViewTab('en');
    setShowViewModal(true);
  };

  // Open Delete Modal
  const handleOpenDelete = asset => {
    setDeletingAsset(asset);
    setShowDeleteModal(true);
  };

  // Form Field Change Handler
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value,
      };
      if (field === 'code') {
        updated.multiple = Boolean(ASSET_CODE_MULTIPLE_MAP[value]);
      }
      return updated;
    });
  };

  // Handle File Selection (deferred upload on submit)
  const handleFileSelect = (e, language = 'en') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (language === 'en') {
      setSelectedFileEn(file);
      handleFormChange('contentPath', file.name);
    } else {
      setSelectedFileIt(file);
      handleFormChange('contentPathIt', file.name);
    }
  };

  // Submit Create / Edit Form - Trigger Confirmation
  const handleFormSubmit = e => {
    e.preventDefault();
    setFormError(null);

    if (!formData.code.trim()) {
      setFormError('Asset Code is required');
      return;
    }
    if (formData.type === 'T' && !formData.contentValue.trim()) {
      setFormError('Text Content / Body (EN) is required');
      return;
    }
    if (formData.type === 'F' && !selectedFileEn && !formData.contentPath.trim()) {
      setFormError('File Attachment (EN) is required');
      return;
    }

    setShowSubmitConfirmModal(true);
  };

  // Execute Submit after Confirmation
  const handleExecuteSubmit = async () => {
    setFormSubmitting(true);
    try {
      let finalContentPath = formData.contentPath.trim();
      let finalContentPathIt = formData.contentPathIt.trim();

      // Upload EN file if user selected one
      if (selectedFileEn) {
        setUploadingFileEn(true);
        const resEn = await assetService.uploadFile(selectedFileEn);
        if (resEn && resEn.data && resEn.data.path) {
          finalContentPath = resEn.data.path;
        }
        setUploadingFileEn(false);
      }

      // Upload IT file if user selected one
      if (selectedFileIt) {
        setUploadingFileIt(true);
        const resIt = await assetService.uploadFile(selectedFileIt);
        if (resIt && resIt.data && resIt.data.path) {
          finalContentPathIt = resIt.data.path;
        }
        setUploadingFileIt(false);
      }

      const payload = {
        ...formData,
        code: formData.code.trim(),
        title: formData.title.trim(),
        titleIt: formData.titleIt.trim(),
        description: formData.description.trim(),
        descriptionIt: formData.descriptionIt.trim(),
        contentPath: finalContentPath,
        contentPathIt: finalContentPathIt,
        number: Number(formData.number) || 1,
        version: formData.version.trim() || '1.0.0',
      };

      if (isEditing && editingId) {
        await assetService.updateAsset(editingId, payload);
        setSuccessMessage(`Asset "${payload.code}" updated successfully.`);
      } else {
        await assetService.createAsset(payload);
        setSuccessMessage(`Asset "${payload.code}" created successfully.`);
      }

      setShowSubmitConfirmModal(false);
      setShowFormModal(false);
      fetchAssets();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Failed to save asset:', err);
      setFormError(err.message || 'Failed to save asset');
      setShowSubmitConfirmModal(false);
    } finally {
      setUploadingFileEn(false);
      setUploadingFileIt(false);
      setFormSubmitting(false);
    }
  };

  // Submit Delete
  const handleDeleteSubmit = async () => {
    if (!deletingAsset) return;

    setDeleteSubmitting(true);
    try {
      await assetService.deleteAsset(deletingAsset.id);
      setSuccessMessage(`Asset "${deletingAsset.code}" deleted successfully.`);
      setShowDeleteModal(false);
      setDeletingAsset(null);
      fetchAssets();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Failed to delete asset:', err);
      setError(`Failed to delete asset: ${err.message || 'Unknown error'}`);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className='p-6 max-w-7xl mx-auto'>
      <PageHeader
        title='Assets Management'
        subtitle='Manage legal policies, static copies, app content, banners, and documents'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Master' },
          { name: 'Assets' },
        ]}
      />

      {/* Success Notification Banner */}
      {successMessage && (
        <div className='mb-6 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg flex items-center justify-between shadow-sm animate-fade-in'>
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5 text-emerald-600' fill='currentColor' viewBox='0 0 20 20'>
              <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
            </svg>
            <span className='text-sm font-medium'>{successMessage}</span>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className='text-emerald-600 hover:text-emerald-800 text-sm font-bold'
          >
            ✕
          </button>
        </div>
      )}

      {/* Error Notification Banner */}
      {error && (
        <div className='mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between shadow-sm'>
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5 text-red-600' fill='currentColor' viewBox='0 0 20 20'>
              <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
            </svg>
            <span className='text-sm font-medium'>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className='text-red-600 hover:text-red-800 text-sm font-bold'
          >
            ✕
          </button>
        </div>
      )}

      {/* Filter & Action Toolbar */}
      <div className='bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6'>
        <form onSubmit={handleApplyFilter} className='flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between'>
          <div className='flex flex-col sm:flex-row gap-3 flex-1'>
            {/* Search Input */}
            <div className='relative flex-1'>
              <div className='absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none'>
                <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                </svg>
              </div>
              <input
                type='text'
                placeholder='Search code, title, or description...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors'
              />
            </div>

            {/* Type Filter */}
            <div className='w-full sm:w-44'>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors'
              >
                <option value=''>All Types</option>
                <option value='T'>Text / Copy (T)</option>
                <option value='F'>File / Document (F)</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className='w-full sm:w-40'>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors'
              >
                <option value=''>All Status</option>
                <option value='A'>Active</option>
                <option value='N'>Inactive</option>
              </select>
            </div>

            {/* Filter Buttons */}
            <div className='flex gap-2'>
              <button
                type='submit'
                className='px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors shadow-sm'
              >
                Filter
              </button>
              {(searchTerm || filterType || filterStatus) && (
                <button
                  type='button'
                  onClick={handleResetFilter}
                  className='px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Create Button */}
          <div className='flex justify-end'>
            <button
              type='button'
              onClick={handleOpenCreate}
              className='inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
              </svg>
              <span>Add New Asset</span>
            </button>
          </div>
        </form>
      </div>

      {/* Assets Table Container */}
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 text-left text-sm'>
            <thead className='bg-gray-50 border-b border-gray-200'>
              <tr>
                <th scope='col' className='px-6 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider'>
                  Asset Code & Title
                </th>
                <th scope='col' className='px-4 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider'>
                  Type
                </th>
                <th scope='col' className='px-4 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider'>
                  Version
                </th>
                <th scope='col' className='px-4 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider'>
                  Status
                </th>
                <th scope='col' className='px-4 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider'>
                  Required
                </th>
                <th scope='col' className='px-4 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider'>
                  CTA
                </th>
                <th scope='col' className='px-6 py-3.5 font-semibold text-gray-700 text-xs uppercase tracking-wider text-right'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white'>
              {loading ? (
                // Loading Skeleton Rows
                [...Array(5)].map((_, i) => (
                  <tr key={i} className='animate-pulse'>
                    <td className='px-6 py-4'>
                      <div className='h-4 bg-gray-200 rounded w-32 mb-2'></div>
                      <div className='h-3 bg-gray-100 rounded w-48'></div>
                    </td>
                    <td className='px-4 py-4'><div className='h-5 bg-gray-200 rounded w-16'></div></td>
                    <td className='px-4 py-4'><div className='h-4 bg-gray-200 rounded w-12'></div></td>
                    <td className='px-4 py-4'><div className='h-5 bg-gray-200 rounded w-16'></div></td>
                    <td className='px-4 py-4'><div className='h-4 bg-gray-200 rounded w-10'></div></td>
                    <td className='px-4 py-4'><div className='h-4 bg-gray-200 rounded w-16'></div></td>
                    <td className='px-6 py-4 text-right'><div className='h-8 bg-gray-200 rounded w-20 ml-auto'></div></td>
                  </tr>
                ))
              ) : assets.length === 0 ? (
                <tr>
                  <td colSpan={7} className='px-6 py-12 text-center text-gray-500'>
                    <div className='flex flex-col items-center justify-center'>
                      <svg className='w-12 h-12 text-gray-300 mb-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                      </svg>
                      <p className='text-base font-semibold text-gray-700'>No assets found</p>
                      <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                        {searchTerm || filterType || filterStatus
                          ? 'Try adjusting your search criteria or resetting filters.'
                          : 'Get started by creating your first platform asset.'}
                      </p>
                      {!searchTerm && !filterType && !filterStatus && (
                        <button
                          type='button'
                          onClick={handleOpenCreate}
                          className='mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors'
                        >
                          Add Asset
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                assets.map(asset => (
                  <tr key={asset.id} className='hover:bg-gray-50/80 transition-colors'>
                    <td className='px-6 py-4'>
                      <div className='flex flex-col'>
                        <div className='flex items-center gap-2'>
                          <span className='font-mono font-bold text-gray-900 text-sm'>
                            {asset.code}
                          </span>
                          {asset.multiple && (
                            <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200'>
                              Multi
                            </span>
                          )}
                        </div>
                        <span className='text-sm text-gray-600 font-medium mt-0.5'>
                          {asset.title || '—'}
                        </span>
                        {asset.titleIt && (
                          <span className='text-xs text-gray-400 italic'>
                            IT: {asset.titleIt}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      {asset.type === 'F' ? (
                        <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200'>
                          <svg className='w-3.5 h-3.5 text-emerald-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' />
                          </svg>
                          File
                        </span>
                      ) : (
                        <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200'>
                          <svg className='w-3.5 h-3.5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                          </svg>
                          Text
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='flex flex-col'>
                        <span className='text-xs font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded w-max'>
                          v{asset.version || '1.0.0'}
                        </span>
                        <span className='text-[11px] text-gray-500 mt-0.5'>
                          Seq #{asset.number || 1}
                        </span>
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <button
                        type='button'
                        onClick={e => handleToggleStatus(asset, e)}
                        disabled={togglingStatusId === asset.id}
                        title={`Click to set ${asset.status === 'A' ? 'Inactive' : 'Active'}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer hover:shadow-sm ${
                          asset.status === 'A'
                            ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                            : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                        } ${togglingStatusId === asset.id ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        {togglingStatusId === asset.id ? (
                          <span className='w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin'></span>
                        ) : (
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              asset.status === 'A' ? 'bg-green-600' : 'bg-gray-400'
                            }`}
                          ></span>
                        )}
                        <span>{asset.status === 'A' ? 'Active' : 'Inactive'}</span>
                      </button>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      {asset.required ? (
                        <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200'>
                          Required
                        </span>
                      ) : (
                        <span className='text-xs text-gray-400'>Optional</span>
                      )}
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      {asset.ctaType ? (
                        <span className='text-xs text-gray-700 font-mono bg-gray-50 px-2 py-1 rounded border border-gray-200' title={asset.ctaUrl || ''}>
                          {asset.ctaType === 'A' ? 'In-App' : asset.ctaType === 'W' ? 'Web View' : 'External'}
                        </span>
                      ) : (
                        <span className='text-xs text-gray-400'>None</span>
                      )}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <div className='flex items-center justify-end gap-1.5'>
                        {/* Status Toggle CTA Action */}
                        <button
                          type='button'
                          onClick={e => handleToggleStatus(asset, e)}
                          disabled={togglingStatusId === asset.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            asset.status === 'A'
                              ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                              : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                          }`}
                          title={asset.status === 'A' ? 'Deactivate asset' : 'Activate asset'}
                        >
                          {togglingStatusId === asset.id ? (
                            <div className='w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin'></div>
                          ) : asset.status === 'A' ? (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' />
                            </svg>
                          ) : (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                            </svg>
                          )}
                        </button>

                        {/* View Action */}
                        <button
                          type='button'
                          onClick={() => handleOpenView(asset)}
                          className='p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                          title='View details'
                        >
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                          </svg>
                        </button>

                        {/* Edit Action */}
                        <button
                          type='button'
                          onClick={() => handleOpenEdit(asset)}
                          className='p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors'
                          title='Edit asset'
                        >
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                          </svg>
                        </button>

                        {/* Delete Action */}
                        <button
                          type='button'
                          onClick={() => handleOpenDelete(asset)}
                          className='p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                          title='Delete asset'
                        >
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <div className='px-6 py-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4'>
          <div className='text-sm text-gray-600'>
            Showing <span className='font-semibold'>{assets.length > 0 ? (pagination.page - 1) * pagination.size + 1 : 0}</span> to{' '}
            <span className='font-semibold'>
              {Math.min(pagination.page * pagination.size, pagination.total)}
            </span>{' '}
            of <span className='font-semibold'>{pagination.total}</span> assets
          </div>

          <div className='flex items-center gap-2'>
            <button
              type='button'
              disabled={pagination.page <= 1 || loading}
              onClick={() => fetchAssets({ page: pagination.page - 1 })}
              className='px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              Previous
            </button>
            <span className='px-3 py-1.5 text-sm text-gray-700 font-medium'>
              Page {pagination.page} of {Math.max(1, pagination.pageCount)}
            </span>
            <button
              type='button'
              disabled={pagination.page >= pagination.pageCount || loading}
              onClick={() => fetchAssets({ page: pagination.page + 1 })}
              className='px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      {showFormModal && (
        <div className='fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in'>
          <div className='bg-white rounded-2xl max-w-3xl w-full shadow-2xl border border-gray-200 overflow-hidden'>
            {/* Modal Header */}
            <div className='px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between'>
              <div>
                <h3 className='text-lg font-bold text-gray-900'>
                  {isEditing ? `Edit Asset: ${formData.code}` : 'Create New Platform Asset'}
                </h3>
                <p className='text-xs text-gray-500 mt-0.5'>
                  Configure static document parameters, localized text copy, versioning, and CTA.
                </p>
              </div>
              <button
                type='button'
                onClick={() => setShowFormModal(false)}
                className='text-gray-400 hover:text-gray-600 text-xl font-bold p-1'
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit}>
              <div className='p-6 space-y-6 max-h-[75vh] overflow-y-auto'>
                {formError && (
                  <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                    {formError}
                  </div>
                )}

                {/* Section 1: Core Identification */}
                <div className='space-y-4'>
                  <h4 className='text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    1. Core Information
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <div className='flex items-center justify-between mb-1'>
                        <label className='block text-xs font-semibold text-gray-700'>
                          Asset Code <span className='text-red-500'>*</span>
                        </label>
                        {loadingVersionInfo && (
                          <span className='text-[10px] text-blue-600 animate-pulse font-medium'>
                            Calculating next seq...
                          </span>
                        )}
                      </div>
                      <select
                        value={formData.code}
                        onChange={e => handleCodeChange(e.target.value)}
                        required
                        className='w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      >
                        <option value=''>Select Asset Code</option>
                        {ASSET_CODE_OPTIONS.map(codeOption => (
                          <option key={codeOption} value={codeOption}>
                            {codeOption}
                          </option>
                        ))}
                        {formData.code && !ASSET_CODE_OPTIONS.includes(formData.code) && (
                          <option value={formData.code}>{formData.code}</option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Status <span className='text-red-500'>*</span>
                      </label>
                      <select
                        value={formData.status}
                        onChange={e => handleFormChange('status', e.target.value)}
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      >
                        <option value='A'>Active</option>
                        <option value='N'>Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className='space-y-3 pt-1'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div className='flex items-center justify-between text-sm text-gray-700 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200'>
                        <span className='text-xs font-semibold text-gray-600'>Type:</span>
                        {formData.type === 'T' ? (
                          <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200'>
                            <span className='w-1.5 h-1.5 rounded-full bg-blue-600'></span>
                            Text / Copy
                          </span>
                        ) : (
                          <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200'>
                            <span className='w-1.5 h-1.5 rounded-full bg-emerald-600'></span>
                            File / Document
                          </span>
                        )}
                      </div>

                      <div className='flex items-center justify-between text-sm text-gray-700 bg-gray-50 px-3.5 py-2.5 rounded-lg border border-gray-200'>
                        <span className='text-xs font-semibold text-gray-600'>Return Mode:</span>
                        {formData.multiple ? (
                          <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200'>
                            <span className='w-1.5 h-1.5 rounded-full bg-indigo-600'></span>
                            Multiple Asset Returns
                          </span>
                        ) : (
                          <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200'>
                            <span className='w-1.5 h-1.5 rounded-full bg-gray-500'></span>
                            Single Asset Return
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className='inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none'>
                        <input
                          type='checkbox'
                          checked={formData.required}
                          onChange={e => handleFormChange('required', e.target.checked)}
                          className='w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500'
                        />
                        <span>Require User Acknowledgement</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Section 2: English Content */}
                <div className='space-y-4 pt-2 border-t border-gray-200'>
                  <h4 className='text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5'>
                    <span>🇬🇧 2. Content (English)</span>
                  </h4>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Title (EN)
                      </label>
                      <input
                        type='text'
                        placeholder='e.g. Terms of Service'
                        value={formData.title}
                        onChange={e => handleFormChange('title', e.target.value)}
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Description (EN)
                      </label>
                      <input
                        type='text'
                        placeholder='Short summary or subtitle'
                        value={formData.description}
                        onChange={e => handleFormChange('description', e.target.value)}
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                  </div>

                  {formData.type === 'T' ? (
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Text Content / Body (EN) <span className='text-red-500'>*</span>
                      </label>
                      <textarea
                        rows={4}
                        placeholder='Enter text or markdown content...'
                        value={formData.contentValue}
                        onChange={e => handleFormChange('contentValue', e.target.value)}
                        required
                        className='w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                  ) : (
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        File Attachment (EN) <span className='text-red-500'>*</span>
                      </label>
                      <div className='flex items-center gap-3'>
                        <input
                          type='text'
                          placeholder='Choose a file or enter path...'
                          value={selectedFileEn ? selectedFileEn.name : formData.contentPath}
                          onChange={e => {
                            setSelectedFileEn(null);
                            handleFormChange('contentPath', e.target.value);
                          }}
                          required
                          className='flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                        />
                        <label className='px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg cursor-pointer transition-colors border border-gray-300'>
                          <span>{selectedFileEn ? 'Change File' : 'Choose File'}</span>
                          <input type='file' accept='image/*,application/pdf' onChange={e => handleFileSelect(e, 'en')} className='hidden' />
                        </label>
                      </div>
                      <p className='text-[11px] text-gray-400 mt-1 flex items-center justify-between'>
                        <span>Supported: Images (PNG, JPG, WEBP, GIF, SVG) and PDF documents</span>
                        {selectedFileEn && (
                          <span className='text-blue-600 font-medium'>
                            ✓ Ready to upload on submit
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Section 3: Italian Content */}
                <div className='space-y-4 pt-2 border-t border-gray-200'>
                  <h4 className='text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5'>
                    <span>🇮🇹 3. Content (Italian - Optional)</span>
                  </h4>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Title (IT)
                      </label>
                      <input
                        type='text'
                        placeholder='e.g. Termini di Servizio'
                        value={formData.titleIt}
                        onChange={e => handleFormChange('titleIt', e.target.value)}
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Description (IT)
                      </label>
                      <input
                        type='text'
                        placeholder='Breve descrizione'
                        value={formData.descriptionIt}
                        onChange={e => handleFormChange('descriptionIt', e.target.value)}
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                  </div>

                  {formData.type === 'T' ? (
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Text Content / Body (IT)
                      </label>
                      <textarea
                        rows={4}
                        placeholder='Inserisci contenuto in italiano...'
                        value={formData.contentValueIt}
                        onChange={e => handleFormChange('contentValueIt', e.target.value)}
                        className='w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                  ) : (
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        File Attachment (IT)
                      </label>
                      <div className='flex items-center gap-3'>
                        <input
                          type='text'
                          placeholder='Choose a file or enter path...'
                          value={selectedFileIt ? selectedFileIt.name : formData.contentPathIt}
                          onChange={e => {
                            setSelectedFileIt(null);
                            handleFormChange('contentPathIt', e.target.value);
                          }}
                          className='flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                        />
                        <label className='px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg cursor-pointer transition-colors border border-gray-300'>
                          <span>{selectedFileIt ? 'Change File' : 'Choose File'}</span>
                          <input type='file' accept='image/*,application/pdf' onChange={e => handleFileSelect(e, 'it')} className='hidden' />
                        </label>
                      </div>
                      <p className='text-[11px] text-gray-400 mt-1 flex items-center justify-between'>
                        <span>Supported: Images (PNG, JPG, WEBP, GIF, SVG) and PDF documents</span>
                        {selectedFileIt && (
                          <span className='text-blue-600 font-medium'>
                            ✓ Ready to upload on submit
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Section 4: Versioning & CTA */}
                <div className='space-y-4 pt-2 border-t border-gray-200'>
                  <h4 className='text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    4. Versioning & Call To Action (CTA)
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Version String
                      </label>
                      <input
                        type='text'
                        placeholder='1.0.0'
                        value={formData.version}
                        onChange={e => handleFormChange('version', e.target.value)}
                        className='w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        Seq Number
                      </label>
                      <input
                        type='number'
                        min={1}
                        placeholder='1'
                        value={formData.number}
                        onChange={e => handleFormChange('number', parseInt(e.target.value) || 1)}
                        className='w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        CTA Action Type
                      </label>
                      <select
                        value={formData.ctaType}
                        onChange={e => handleFormChange('ctaType', e.target.value)}
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white'
                      >
                        <option value=''>None</option>
                        <option value='A'>A - In-App Route</option>
                        <option value='W'>W - Web View</option>
                        <option value='X'>X - External Browser</option>
                      </select>
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 mb-1'>
                        CTA URL / Route
                      </label>
                      <input
                        type='text'
                        placeholder='/settings or https://...'
                        value={formData.ctaUrl}
                        onChange={e => handleFormChange('ctaUrl', e.target.value)}
                        className='w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className='px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3'>
                <button
                  type='button'
                  onClick={() => setShowFormModal(false)}
                  disabled={formSubmitting}
                  className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={formSubmitting}
                  className='px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors flex items-center gap-2'
                >
                  {formSubmitting && (
                    <svg className='animate-spin h-4 w-4 text-white' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v8H4z'></path>
                    </svg>
                  )}
                  <span>
                    {formSubmitting
                      ? (uploadingFileEn || uploadingFileIt ? 'Uploading Files...' : 'Saving Asset...')
                      : (isEditing ? 'Save Changes' : 'Create Asset')}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW / PREVIEW MODAL */}
      {showViewModal && viewingAsset && (
        <div className='fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in'>
          <div className='bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-gray-200 overflow-hidden'>
            <div className='px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <span className='font-mono font-bold text-gray-900 text-base'>
                  {viewingAsset.code}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${viewingAsset.type === 'F' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                  {viewingAsset.type === 'F' ? 'File Asset' : 'Text Asset'}
                </span>
              </div>
              <button
                type='button'
                onClick={() => setShowViewModal(false)}
                className='text-gray-400 hover:text-gray-600 text-xl font-bold p-1'
              >
                ✕
              </button>
            </div>

            <div className='p-6 space-y-5 max-h-[75vh] overflow-y-auto'>
              {/* Metadata Info Grid */}
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 p-3.5 rounded-xl border border-gray-200 text-xs'>
                <div>
                  <span className='text-gray-400 block'>Version</span>
                  <span className='font-semibold text-gray-800'>v{viewingAsset.version || '1.0.0'} (#{viewingAsset.number || 1})</span>
                </div>
                <div>
                  <span className='text-gray-400 block'>Status</span>
                  <span className='font-semibold text-gray-800'>{viewingAsset.status === 'A' ? 'Active' : 'Inactive'}</span>
                </div>
                <div>
                  <span className='text-gray-400 block'>Required</span>
                  <span className='font-semibold text-gray-800'>{viewingAsset.required ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className='text-gray-400 block'>CTA Type</span>
                  <span className='font-semibold text-gray-800'>{viewingAsset.ctaType || 'None'}</span>
                </div>
              </div>

              {/* Language Tabs */}
              <div className='border-b border-gray-200 flex gap-4'>
                <button
                  type='button'
                  onClick={() => setViewTab('en')}
                  className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${viewTab === 'en' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  🇬🇧 English Content
                </button>
                <button
                  type='button'
                  onClick={() => setViewTab('it')}
                  className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${viewTab === 'it' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  🇮🇹 Italian Content
                </button>
              </div>

              {/* Tab Content */}
              {viewTab === 'en' ? (
                <div className='space-y-3'>
                  <div>
                    <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block'>Title</label>
                    <p className='text-sm font-medium text-gray-900 mt-0.5'>{viewingAsset.title || '—'}</p>
                  </div>
                  <div>
                    <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block'>Description</label>
                    <p className='text-sm text-gray-600 mt-0.5'>{viewingAsset.description || '—'}</p>
                  </div>

                  {viewingAsset.type === 'T' ? (
                    <div>
                      <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1'>Content Value</label>
                      <pre className='p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono whitespace-pre-wrap max-h-60 overflow-y-auto'>
                        {viewingAsset.contentValue || '(Empty text content)'}
                      </pre>
                    </div>
                  ) : (
                    <div>
                      <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1'>File Path & URL</label>
                      <div className='p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs flex flex-col gap-2'>
                        <span className='font-mono text-gray-700'>{viewingAsset.contentPath || '—'}</span>
                        {viewingAsset.contentPathUrl && (
                          <a
                            href={viewingAsset.contentPathUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:underline font-semibold inline-flex items-center gap-1'
                          >
                            <span>Open / Download Attached File ↗</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className='space-y-3'>
                  <div>
                    <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block'>Title (IT)</label>
                    <p className='text-sm font-medium text-gray-900 mt-0.5'>{viewingAsset.titleIt || '—'}</p>
                  </div>
                  <div>
                    <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block'>Description (IT)</label>
                    <p className='text-sm text-gray-600 mt-0.5'>{viewingAsset.descriptionIt || '—'}</p>
                  </div>

                  {viewingAsset.type === 'T' ? (
                    <div>
                      <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1'>Content Value (IT)</label>
                      <pre className='p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono whitespace-pre-wrap max-h-60 overflow-y-auto'>
                        {viewingAsset.contentValueIt || '(Empty text content)'}
                      </pre>
                    </div>
                  ) : (
                    <div>
                      <label className='text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1'>File Path & URL (IT)</label>
                      <div className='p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs flex flex-col gap-2'>
                        <span className='font-mono text-gray-700'>{viewingAsset.contentPathIt || '—'}</span>
                        {viewingAsset.contentPathItUrl && (
                          <a
                            href={viewingAsset.contentPathItUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:underline font-semibold inline-flex items-center gap-1'
                          >
                            <span>Open / Download Attached File (IT) ↗</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end'>
              <button
                type='button'
                onClick={() => setShowViewModal(false)}
                className='px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUBMIT CONFIRMATION MODAL */}
      {showSubmitConfirmModal && (
        <div className='fixed inset-0 z-[60] overflow-y-auto bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in'>
          <div className='bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden p-6'>
            <div className='flex items-center gap-3 text-blue-600 mb-4'>
              <div className='w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center'>
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </div>
              <h3 className='text-lg font-bold text-gray-900'>
                {isEditing ? 'Confirm Update Asset' : 'Confirm Create Asset'}
              </h3>
            </div>

            <p className='text-sm text-gray-600 mb-4'>
              Are you sure you want to {isEditing ? 'save changes to' : 'create'} this asset?
            </p>

            <div className='bg-gray-50 rounded-xl p-3.5 border border-gray-200 text-xs space-y-2 mb-6'>
              <div className='flex justify-between'>
                <span className='text-gray-500 font-medium'>Asset Code:</span>
                <span className='font-mono font-bold text-gray-900'>{formData.code}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-500 font-medium'>Type:</span>
                <span className='font-semibold text-gray-800'>{formData.type === 'T' ? 'Text / Copy' : 'File / Document'}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-500 font-medium'>Version & Seq:</span>
                <span className='font-mono text-gray-800'>v{formData.version || '1.0.0'} (#{formData.number || 1})</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-500 font-medium'>Status:</span>
                <span className={`font-semibold ${formData.status === 'A' ? 'text-green-600' : 'text-gray-600'}`}>
                  {formData.status === 'A' ? 'Active' : 'Inactive'}
                </span>
              </div>
              {selectedFileEn && (
                <div className='flex justify-between text-blue-700 font-medium border-t border-gray-200 pt-1.5'>
                  <span>Upload (EN):</span>
                  <span className='truncate max-w-[180px] font-mono'>{selectedFileEn.name}</span>
                </div>
              )}
              {selectedFileIt && (
                <div className='flex justify-between text-blue-700 font-medium'>
                  <span>Upload (IT):</span>
                  <span className='truncate max-w-[180px] font-mono'>{selectedFileIt.name}</span>
                </div>
              )}
            </div>

            <div className='flex items-center justify-end gap-3'>
              <button
                type='button'
                disabled={formSubmitting}
                onClick={() => setShowSubmitConfirmModal(false)}
                className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors'
              >
                Cancel
              </button>
              <button
                type='button'
                disabled={formSubmitting}
                onClick={handleExecuteSubmit}
                className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50'
              >
                {formSubmitting && (
                  <svg className='animate-spin h-4 w-4 text-white' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v8H4z'></path>
                  </svg>
                )}
                <span>
                  {formSubmitting
                    ? (uploadingFileEn || uploadingFileIt ? 'Uploading Files...' : 'Saving...')
                    : (isEditing ? 'Confirm & Update' : 'Confirm & Create')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && deletingAsset && (
        <div className='fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in'>
          <div className='bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden p-6'>
            <div className='flex items-center gap-3 text-red-600 mb-4'>
              <div className='w-10 h-10 rounded-full bg-red-100 flex items-center justify-center'>
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
                </svg>
              </div>
              <h3 className='text-lg font-bold text-gray-900'>Delete Asset</h3>
            </div>

            <p className='text-sm text-gray-600 mb-6'>
              Are you sure you want to delete the asset with code{' '}
              <span className='font-mono font-bold text-gray-900'>{deletingAsset.code}</span> (
              {deletingAsset.title})? This action cannot be undone.
            </p>

            <div className='flex items-center justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteSubmitting}
                className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleDeleteSubmit}
                disabled={deleteSubmitting}
                className='px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-2'
              >
                {deleteSubmitting && (
                  <svg className='animate-spin h-4 w-4 text-white' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v8H4z'></path>
                  </svg>
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterAssetsPage;
